import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ExpandToggle } from "@/components/ExpandToggle";
import { SellThroughRow, SizeYieldTable } from "@/components/SellThroughRow";
import { Loader2 } from "lucide-react";
import { CURRENT_SEASON } from "@/lib/season";

const Pills = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1">
    {items.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`rounded-full border px-3 py-1 text-xs transition-colors ${value === k ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>{l}</button>
    ))}
  </div>
);

const ColorYield = ({ rows }) => (
  <div className="space-y-2.5" data-testid="color-yield-table">
    {rows.map((c) => (
      <div key={c.name}>
        <div className="mb-1 flex justify-between text-xs"><span className="font-medium">{c.name}</span>
          <span className="text-muted-foreground">{num(c.sold)}/{num(c.produced)} · {c.ratio}%</span></div>
        <Progress value={Math.min(100, c.ratio)} className="h-1.5" />
      </div>
    ))}
  </div>
);

export const ProductionYield = ({ season: linked }) => {
  const [season, setSeason] = useState(linked || CURRENT_SEASON);
  useEffect(() => { if (linked) { setSeason(linked); } }, [linked]);
  const [view, setView] = useState("size");
  const [all, setAll] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["production-yield", season],
    queryFn: async () => (await api.get("/sales/production-yield", { params: { season } })).data });
  const seasons = [["tutte", "Tutte"], ...(data?.seasons || [CURRENT_SEASON]).map((s) => [s, s])];
  const summary = view === "size" ? data?.size_yield : data?.color_yield;
  return (
    <Card className="p-6 lg:col-span-3" data-testid="production-yield">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Resa di produzione · Prodotto vs Venduto</h3>
          <p className="text-xs text-muted-foreground">Su tutta la stagione, venduto al netto dei resi · non dipende dal periodo scelto in alto</p>
        </div>
        <Pills items={seasons} value={season} onChange={(s) => { setSeason(s); setAll(false); }} prefix="yield-season" />
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="space-y-8">
          <div>
            <div className="space-y-4">{(all ? data.items : data.items.slice(0, 15)).map((s) => <SellThroughRow key={s.sku} s={s} />)}</div>
            {!data.items.length && <p className="text-sm text-muted-foreground">Nessun capo per questa stagione.</p>}
            <ExpandToggle total={data.items.length} open={all} onToggle={() => setAll(!all)} testid="sellthrough-toggle" />
          </div>
          <div className="border-t border-border/60 pt-6" data-testid="yield-summary">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Riepilogo stagione</p>
              <Pills items={[["size", "Per taglia"], ["color", "Per colore"]]} value={view} onChange={setView} prefix="yield-view" />
            </div>
            {summary?.length ? (view === "size" ? <SizeYieldTable rows={summary} testid="size-yield-table" /> : <ColorYield rows={summary} />) : (
              <p className="text-sm text-muted-foreground" data-testid="yield-summary-empty">
                {view === "size" ? "Nessuna taglia prodotta registrata per questa stagione."
                  : "Nessuna quantità prodotta registrata per questa stagione."}</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
